import React, { useEffect, useMemo, useState } from 'react';
import {
    ActivityIndicator,
    Modal,
    ScrollView,
    StyleSheet,
    Text,
    TextInput,
    TouchableOpacity,
    View,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import twoFactorAuth from '../services/twoFactorAuth';
import { getOrCreateDeviceTrustId, setDeviceTrustToken } from '../helpers/storage';
import { ModalKeyboardAvoider } from './KeyboardAware';
import {
    COLORS,
    CountdownRing,
    OtpInput,
    RememberDeviceRow,
    TwoFactorBrand,
} from './twoFactor/TwoFactorUI';

const SUPPORTED_METHODS = ['totp', 'email', 'sms', 'recovery'];
const RESEND_SECONDS = 60;

const METHOD_META = (t) => ({
    totp: {
        icon: 'keypad-outline',
        title: t('two_factor_method_auth_app', { defaultValue: 'Authenticator app' }),
        description: t('two_factor_method_auth_app_help', { defaultValue: 'Use your rotating 6-digit code.' }),
    },
    email: {
        icon: 'mail-outline',
        title: t('two_factor_method_email', { defaultValue: 'Email code' }),
        description: t('two_factor_method_email_help', { defaultValue: 'Send a code to your inbox.' }),
    },
    sms: {
        icon: 'chatbubble-ellipses-outline',
        title: t('two_factor_method_sms', { defaultValue: 'SMS code' }),
        description: t('two_factor_method_sms_help', { defaultValue: 'Send a code to your phone.' }),
    },
    recovery: {
        icon: 'key-outline',
        title: t('two_factor_method_recovery', { defaultValue: 'Recovery code' }),
        description: t('two_factor_method_recovery_help', { defaultValue: 'Use one of your backup codes.' }),
    },
});

const challengeSubtitle = (method, t, hint, deliveredChannel) => {
    if (method === 'sms') {
        // The backend may deliver the SMS-method code over WhatsApp when the
        // number is reachable there (falls back to SMS otherwise), or \u2014 when
        // the SMS provider is out of credit \u2014 over email instead.
        if (deliveredChannel === 'whatsapp') {
            return t('two_factor_enter_whatsapp_code', {
                defaultValue: 'Enter the code we sent on WhatsApp to {{phone}}.',
                phone: hint?.phone || '\u2022\u2022\u2022\u2022',
            });
        }
        if (deliveredChannel === 'email') {
            return t('two_factor_sms_fallback_email', {
                defaultValue: "SMS service issue \u2014 we've e-mailed your code to {{hint}} instead. Please check your mailbox (and spam folder).",
                hint: hint?.email || '\u2022\u2022\u2022',
            });
        }

        return t('two_factor_enter_sms_code', {
            defaultValue: 'Enter the code we texted to {{phone}}.',
            phone: hint?.phone || '\u2022\u2022\u2022\u2022',
        });
    }
    if (method === 'email') {
        return t('two_factor_enter_email_code', {
            defaultValue: 'Enter the code we emailed to {{email}}.',
            email: hint?.email || '\u2022\u2022\u2022',
        });
    }
    if (method === 'recovery') {
        return t('two_factor_enter_recovery_code', { defaultValue: 'Enter one of your saved recovery codes.' });
    }

    return t('two_factor_enter_auth_code', { defaultValue: 'Enter the 6-digit code from your authenticator app.' });
};

export default function TwoFactorChallengeModal({
    visible,
    payload,
    t,
    onSuccess,
    onCancel,
}) {
    const [method, setMethod] = useState(null);
    const [code, setCode] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [rememberDevice, setRememberDevice] = useState(false);
    const [resendKey, setResendKey] = useState(0);
    const [canResend, setCanResend] = useState(false);
    // Channel the server actually delivered the SMS-method code over ('whatsapp' | 'sms' | 'email').
    const [deliveredChannel, setDeliveredChannel] = useState(null);
    // Masked hint ({ phone, email? }) for the channel actually used — captured from the
    // send response so an SMS→email fallback can surface the masked e-mail it went to.
    const [deliveredHint, setDeliveredHint] = useState(null);

    const labels = useMemo(() => METHOD_META(t), [t]);

    const availableMethods = useMemo(() => {
        const list = Array.isArray(payload?.methods) ? payload.methods : [];

        return list.filter((m) => SUPPORTED_METHODS.includes(m));
    }, [payload?.methods]);

    const sendOtp = async (nextMethod) => {
        if (nextMethod === 'email') {
            await twoFactorAuth.challengeEmailSend(payload.preAuthToken);

            return null;
        }
        if (nextMethod === 'sms') {
            const res = await twoFactorAuth.challengeSmsSend(payload.preAuthToken);

            return { channel: res?.channel || 'sms', hint: res?.hint || null };
        }

        return null;
    };

    const chooseMethod = async (nextMethod) => {
        setMethod(nextMethod);
        setCode('');
        setError('');
        setCanResend(false);
        setDeliveredChannel(null);
        setDeliveredHint(null);
        if (nextMethod === 'email' || nextMethod === 'sms') {
            setBusy(true);
            try {
                const delivered = await sendOtp(nextMethod);

                setDeliveredChannel(delivered?.channel || null);
                setDeliveredHint(delivered?.hint || null);
                setResendKey((k) => k + 1);
            } catch (e) {
                setError(e?.response?.data?.message || e?.message || t('two_factor_send_failed', { defaultValue: 'Failed to send code.' }));
            } finally {
                setBusy(false);
            }
        }
    };

    // Auto-select the only / preferred method so the user lands on the code field.
    useEffect(() => {
        if (!visible) {
            return;
        }
        setError('');
        setCode('');
        setRememberDevice(false);
        const preferred = availableMethods.includes(payload?.preferredMethod)
            ? payload.preferredMethod
            : availableMethods[0] || null;

        if (preferred) {
            chooseMethod(preferred);
        } else {
            setMethod(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visible, availableMethods]);

    const resend = async () => {
        setBusy(true);
        setError('');
        try {
            const delivered = await sendOtp(method);

            if (delivered) {
                setDeliveredChannel(delivered.channel || null);
                setDeliveredHint(delivered.hint || null);
            }
            setCanResend(false);
            setResendKey((k) => k + 1);
        } catch (e) {
            setError(e?.response?.data?.message || e?.message || t('two_factor_send_failed', { defaultValue: 'Failed to send code.' }));
        } finally {
            setBusy(false);
        }
    };

    const verify = async (submitCode) => {
        const trimmed = String(submitCode ?? code).trim();

        if (!method || !trimmed) {
            setError(t('two_factor_code_required', { defaultValue: 'Code is required.' }));

            return;
        }

        setBusy(true);
        setError('');
        try {
            const deviceId = rememberDevice ? await getOrCreateDeviceTrustId() : null;
            const response = await twoFactorAuth.challengeVerify(payload.preAuthToken, method, {
                code: trimmed,
                trustDevice: rememberDevice,
                deviceId,
            });

            if (response?.token && response?.user) {
                if (response.deviceTrustToken) {
                    await setDeviceTrustToken(response.deviceTrustToken);
                }
                onSuccess(response);

                return;
            }
            setError(t('two_factor_verify_failed', { defaultValue: 'Verification failed.' }));
        } catch (e) {
            setError(e?.response?.data?.message || e?.message || t('two_factor_verify_failed', { defaultValue: 'Verification failed.' }));
        } finally {
            setBusy(false);
        }
    };

    const isOtpMethod = method === 'totp' || method === 'email' || method === 'sms';
    const showTimer = method === 'email' || method === 'sms';

    return (
        <Modal visible={visible} transparent animationType="fade" onRequestClose={onCancel}>
            <ModalKeyboardAvoider style={styles.overlay} justify="center">
                <ScrollView
                    style={styles.flex}
                    contentContainerStyle={styles.scroll}
                    keyboardShouldPersistTaps="handled"
                    keyboardDismissMode="interactive"
                    showsVerticalScrollIndicator={false}
                >
                    <View style={styles.card}>
                        <View style={styles.header}>
                            <TwoFactorBrand icon="shield-checkmark" />
                            <Text style={styles.eyebrow}>{t('two_factor_eyebrow', { defaultValue: 'Two-factor authentication' })}</Text>
                            <Text style={styles.title}>{t('two_factor_title', { defaultValue: "Verify it's you" })}</Text>
                            {!!method && (
                                <Text style={styles.subtitle}>
                                    {challengeSubtitle(method, t, { ...(payload?.hint || {}), ...(deliveredHint || {}) }, deliveredChannel)}
                                </Text>
                            )}
                        </View>

                        {availableMethods.length > 1 && (
                            <View style={styles.chips}>
                                {availableMethods.map((m) => {
                                    const meta = labels[m] || {};
                                    const active = m === method;

                                    return (
                                        <TouchableOpacity
                                            key={m}
                                            style={[styles.chip, active ? styles.chipActive : null]}
                                            onPress={() => chooseMethod(m)}
                                            disabled={busy}
                                        >
                                            <Ionicons
                                                name={meta.icon || 'lock-closed-outline'}
                                                size={15}
                                                color={active ? '#fff' : COLORS.sub}
                                            />
                                            <Text style={[styles.chipText, active ? styles.chipTextActive : null]}>
                                                {meta.title || m}
                                            </Text>
                                        </TouchableOpacity>
                                    );
                                })}
                            </View>
                        )}

                        {isOtpMethod && (
                            <View style={styles.codeArea}>
                                <OtpInput
                                    value={code}
                                    onChange={setCode}
                                    onComplete={(full) => verify(full)}
                                    length={6}
                                    disabled={busy}
                                />
                                {showTimer && (
                                    <View style={styles.timerWrap}>
                                        <CountdownRing
                                            seconds={RESEND_SECONDS}
                                            runKey={resendKey}
                                            onExpire={() => setCanResend(true)}
                                            onResend={resend}
                                            resendLabel={t('two_factor_resend', { defaultValue: 'Resend code' })}
                                        />
                                        <Text style={styles.timerHint}>
                                            {canResend
                                                ? t('two_factor_resend_hint', { defaultValue: "Didn't get it? Send a new code." })
                                                : t('two_factor_timer_hint', { defaultValue: 'You can request a new code soon.' })}
                                        </Text>
                                    </View>
                                )}
                            </View>
                        )}

                        {method === 'recovery' && (
                            <TextInput
                                value={code}
                                onChangeText={setCode}
                                style={styles.recoveryInput}
                                autoCapitalize="none"
                                placeholder={t('two_factor_recovery_placeholder', { defaultValue: 'abcd-1234-ef56' })}
                                placeholderTextColor={COLORS.sub}
                            />
                        )}

                        <RememberDeviceRow
                            value={rememberDevice}
                            onToggle={setRememberDevice}
                            title={t('two_factor_remember_device', { defaultValue: 'Remember this device' })}
                            subtitle={t('two_factor_remember_device_help', { defaultValue: "Skip 2FA on this device for 30 days. Don't use on shared devices." })}
                        />

                        {!!error && (
                            <View style={styles.errorRow}>
                                <Ionicons name="alert-circle" size={15} color={COLORS.danger} />
                                <Text style={styles.error}>{error}</Text>
                            </View>
                        )}

                        <TouchableOpacity
                            style={[styles.primaryButton, busy ? styles.primaryDisabled : null]}
                            onPress={() => verify()}
                            disabled={busy}
                            activeOpacity={0.9}
                        >
                            {busy
                                ? <ActivityIndicator color="#fff" />
                                : <Text style={styles.primaryText}>{t('two_factor_verify_signin', { defaultValue: 'Verify & sign in' })}</Text>}
                        </TouchableOpacity>

                        <TouchableOpacity onPress={onCancel} disabled={busy} style={styles.cancelBtn}>
                            <Text style={styles.cancel}>{t('cancel', { defaultValue: 'Cancel' })}</Text>
                        </TouchableOpacity>
                    </View>
                </ScrollView>
            </ModalKeyboardAvoider>
        </Modal>
    );
}

const styles = StyleSheet.create({
    flex: {
        flex: 1,
    },
    overlay: {
        flex: 1,
        backgroundColor: 'rgba(15,23,42,0.55)',
        justifyContent: 'center',
    },
    scroll: {
        flexGrow: 1,
        justifyContent: 'center',
        padding: 18,
    },
    card: {
        borderRadius: 24,
        backgroundColor: '#fff',
        padding: 22,
        shadowColor: '#0F172A',
        shadowOpacity: 0.18,
        shadowRadius: 30,
        shadowOffset: { width: 0, height: 18 },
        elevation: 12,
    },
    header: {
        alignItems: 'center',
        marginBottom: 18,
    },
    eyebrow: {
        marginTop: 14,
        fontSize: 11,
        fontWeight: '700',
        letterSpacing: 1,
        textTransform: 'uppercase',
        color: COLORS.brandDark,
    },
    title: {
        marginTop: 4,
        fontSize: 22,
        fontWeight: '800',
        color: COLORS.ink,
    },
    subtitle: {
        marginTop: 8,
        fontSize: 13,
        lineHeight: 19,
        color: COLORS.sub,
        textAlign: 'center',
    },
    chips: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 8,
        justifyContent: 'center',
        marginBottom: 18,
    },
    chip: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
        paddingVertical: 8,
        paddingHorizontal: 12,
        borderRadius: 999,
        borderWidth: 1,
        borderColor: COLORS.line,
        backgroundColor: '#fff',
    },
    chipActive: {
        backgroundColor: COLORS.ink,
        borderColor: COLORS.ink,
    },
    chipText: {
        fontSize: 12,
        fontWeight: '700',
        color: COLORS.sub,
    },
    chipTextActive: {
        color: '#fff',
    },
    codeArea: {
        alignItems: 'center',
        marginBottom: 16,
    },
    timerWrap: {
        alignItems: 'center',
        marginTop: 18,
        gap: 8,
    },
    timerHint: {
        fontSize: 12,
        color: COLORS.sub,
    },
    recoveryInput: {
        borderWidth: 1.5,
        borderColor: COLORS.line,
        borderRadius: 12,
        paddingHorizontal: 14,
        paddingVertical: 13,
        marginBottom: 16,
        color: COLORS.ink,
        fontSize: 16,
        letterSpacing: 1,
    },
    errorRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
        marginTop: 12,
    },
    error: {
        color: COLORS.danger,
        fontSize: 13,
        flex: 1,
    },
    primaryButton: {
        marginTop: 18,
        backgroundColor: COLORS.ink,
        paddingVertical: 15,
        borderRadius: 14,
        alignItems: 'center',
    },
    primaryDisabled: {
        opacity: 0.6,
    },
    primaryText: {
        color: '#fff',
        fontWeight: '800',
        fontSize: 15,
    },
    cancelBtn: {
        marginTop: 6,
        paddingVertical: 12,
        alignItems: 'center',
    },
    cancel: {
        color: COLORS.sub,
        fontWeight: '600',
        fontSize: 14,
    },
});
